'use client';

import { Menu as BaseMenu } from '@base-ui/react/menu';

import { type Component, type ComponentProps } from '../../types/component';
import { tv } from '../../utils/tv';
import { Surface } from '../surface/Surface';

const menuPopupClasses = { root: 'zds-menu-popup-root' } as const;

const menuPopupStyles = tv({
  base: [
    menuPopupClasses.root,
    'grid min-w-50 grid-cols-[auto_1fr_auto]',
    'max-h-[var(--available-height)] overflow-y-auto',
    'py-1 outline-none',
  ],
});

type Side = 'top' | 'bottom' | 'left' | 'right';
type Align = 'start' | 'center' | 'end';

type Placement =
  | `${'top' | 'bottom'}-${'left' | 'center' | 'right'}`
  | `${'left' | 'right'}-${'top' | 'center' | 'bottom'}`;

/**
 * Splits a Placement string (e.g. 'bottom-left', 'right-top') into Base UI's
 * `side` and `align` props.
 *
 * Alignment suffix is always the literal cross-axis direction:
 * - top/bottom sides: left → start, center → center, right → end
 * - left/right sides: top → start, center → center, bottom → end
 */
function splitPlacement(placement: Placement): {
  side: Side;
  align: Align;
} {
  const [side, alignRaw] = placement.split('-') as [
    Side,
    'left' | 'right' | 'top' | 'bottom',
  ];
  const align: Align =
    alignRaw === 'left' || alignRaw === 'top'
      ? 'start'
      : alignRaw === 'right' || alignRaw === 'bottom'
        ? 'end'
        : 'center';
  return { side, align };
}

interface MenuPopupBaseProps {
  /**
   * Popup placement relative to trigger.
   *
   * @default 'bottom-left'
   */
  placement?: Placement;
  /**
   * Pixel distance from trigger.
   *
   * @default 8
   */
  offset?: number;
  /**
   * Disable flip on collision.
   *
   * @default false
   */
  disableFlip?: boolean;
  /**
   * Disable shift on collision.
   *
   * @default false
   */
  disableShift?: boolean;
  /**
   * How the popup manages its DOM presence.
   * - `'lazy'`: starts unmounted; mounts on open, unmounts on close.
   * - `'eager'`: mounted at all times, including before first open.
   *   Required for uncontrolled selection state to persist across open/close
   *   cycles.
   *
   * @default 'lazy'
   */
  mountStrategy?: 'lazy' | 'eager';
}

type MenuPopupProps = ComponentProps<MenuPopupBaseProps, 'div'>;

type MenuPopupComponent = Component<MenuPopupBaseProps, 'div'>;

const MenuPopup: MenuPopupComponent = (props) => {
  const {
    placement = 'bottom-left',
    offset = 8,
    disableFlip = false,
    disableShift = false,
    mountStrategy = 'lazy',
    className,
    children,
    ref,
    ...rest
  } = props;

  const { side, align } = splitPlacement(placement);

  return (
    <BaseMenu.Portal keepMounted={mountStrategy === 'eager'}>
      <BaseMenu.Positioner
        side={side}
        align={align}
        sideOffset={offset}
        collisionAvoidance={{
          side: disableFlip ? 'none' : 'flip',
          align: disableShift ? 'none' : 'shift',
        }}
        className="z-9999"
      >
        <BaseMenu.Popup
          {...rest}
          ref={ref}
          render={<Surface variant="elevated" elevation={1} />}
          className={menuPopupStyles({ className })}
        >
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
};

MenuPopup.displayName = 'Menu.Popup';

export {
  MenuPopup,
  type MenuPopupProps,
  type MenuPopupBaseProps,
  menuPopupClasses,
};
